import {FC, PropsWithChildren, useMemo} from 'react'
import { PaymentButtonHollow } from '../CheckOut/styles'
import { FlowWrapper, PaymentFlowButtons, EmptyLeftSide, FlowChildren } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getCurrentContextName } from 'supporting'
import {selectorOrder, goToNextStep, goToPrevStep} from "../../slices/OrderSlice";
import styled from "styled-components";
import {PaymentButtonWithHint} from "../../components/PaymentButtonWithHint/PaymentButtonWithHint";
import { OrderSelectors } from 'shared/features/QueuePaymenrt/slices/selectors/orderSelectors'
import {FlowType} from "../../constants/flowType";
import {createPortal} from "react-dom";
import { withTheme } from 'styled-components'
import { keyframes } from 'styled-components'

const Spinner = withTheme((props) => {
    const {
      viewBox,
      cx,
      cy,
      radius,
      fill,
      width
    } = props
    return (
      <svg viewBox={viewBox || '22 22 44 44'}>
        <circle 
          cx={cx || '44'} 
          cy={cy || '44'}
          r={radius || '20.2'}
          fill={fill || 'none'} 
          strokeWidth={width || '3.6'}
        >
        </circle>
      </svg>
    );
  })


  const wrapperFrames = keyframes`
  0% {
    // transform: rotate(0deg);
    transform-origin: 50% 50%;
  }
  100% { 
    transform: rotate(360deg);
  }
`

const circleFrames = keyframes`
  0% {
    stroke-dasharray: 1px,200px;
    stroke-dashoffset: 0;
  }
  50% { 
    stroke-dasharray: 100px,200px;
    stroke-dashoffset: -15px;
  }
  100% { 
    stroke-dasharray: 100px,200px;
    stroke-dashoffset: -125px;
  }
`

export const SpinnerWrapper = styled.div<any>`
  animation: 1.4s linear 0s infinite normal none running ${wrapperFrames};
  min-width: 22px;
  width: 22px;
  height: 22px;
  display: inline-block;
  box-sizing: inherit;
  font-size: 1.875rem;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-weight: 400;
  line-height: 1.43;
  margin-left: ${p => p.ml !== undefined ? p.ml : 0}px;
  letter-spacing: 0.01071em;
  svg {
    display: block;
    box-sizing: inherit;
    overflow: hidden;
    font-size: 1.875rem;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 400;
    line-height: 1.43;
    letter-spacing: 0.01071em;
    margin: 0px;
    circle {
      animation: ${circleFrames} 1.4s ease-in-out infinite;  
      stroke-dasharray: 80px, 200px;
      stroke-dashoffset: 0px;
      stroke: currentColor;
      transform-origin: 0px 0px;
    }
  }
`

interface PaymentFlowWrapperProps {
    onPay?: () => void
    onPrev?: () => void
    onNext?: () => void
    nextDisabled?: boolean
    nextPortalSelector?: string
    disableHint?: string
    hidePrev?: boolean
    showSpinner?: boolean
    isPayStep?: boolean
    payTitle?: string
    wrapperProps?: any
    nextButtonRef?: any
    hideNext?: any
    fullWidth?: boolean
}

export const NextButtonComponent: FC<Partial<PropsWithChildren<PaymentFlowWrapperProps>>> = (props) => {
    const { onPay, onNext, nextDisabled, nextPortalSelector, showSpinner, isPayStep, disableHint, payTitle, hideNext, fullWidth } = props
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const flowStep = useSelector(OrderSelectors.flowStepSelector)
    const flowSize = useSelector(OrderSelectors.flowSizeSelector)
    const flowType = useSelector(OrderSelectors.flowTypeSelector)
    const contextName = getCurrentContextName()
    const isZeland = contextName === 'zeland';
    const deliveryAddress = (flowType === FlowType.BUY_PRINTED_SUBSCRIPTION || flowType === FlowType.GIFT_PRINTED_SUBSCRIPTION)

    const onNextStep = () => {
        if (typeof (onNext) === 'function') {
            onNext()
            return
        }
        dispatch(goToNextStep())
    }

    const nextOuter = nextPortalSelector && document.querySelector(nextPortalSelector)
    const nextButtonDefault = isPayStep || (flowStep === flowSize)
        ? <PaymentButtonWithHint
            hintText={disableHint}
            closeToRightCorner
            disabled={nextDisabled}
            onClick={onPay}
            buttonTheme={isZeland ? 'yellow' : undefined}
            isGift={flowType === FlowType.SEND_GIFT}
            fullWidth={fullWidth}
            noWidthLimitation={deliveryAddress}
        >
            {
                showSpinner
                    ? <SpinnerWrapper><Spinner /></SpinnerWrapper>
                    : payTitle || t('payment.pay')
            }
        </PaymentButtonWithHint>
        : <PaymentButtonWithHint
            hintText={disableHint}
            closeToRightCorner
            disabled={nextDisabled}
            onClick={onNextStep}
            isGift={flowType === FlowType.SEND_GIFT}
            fullWidth={fullWidth}
        >
            {t("next")}
        </PaymentButtonWithHint>

    const nextButtonComponent = useMemo(() => {
        if (hideNext) return
        if (nextPortalSelector && !nextOuter) return nextButtonDefault
        return nextOuter
            ? createPortal(nextButtonDefault, nextOuter)
            : nextButtonDefault
    }, [nextPortalSelector, nextOuter, hideNext, nextButtonDefault])

    return <>
        {nextButtonComponent}
    </>
}

export const PaymentFlowWrapper: FC<PropsWithChildren<PaymentFlowWrapperProps>> = (props) => {
    const { children, onPrev, hidePrev, wrapperProps = {} } = props
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { flowStep } = useSelector(selectorOrder)
    const contextName = getCurrentContextName()
    const isZeland = contextName === 'zeland';

    const onPrevStep = () => {
        if (typeof (onPrev) === 'function') {
            onPrev()
            return
        }
        dispatch(goToPrevStep())
    }

    return (
        <FlowWrapper {...wrapperProps}>
            <FlowChildren>
                {children}
            </FlowChildren>
            <PaymentFlowButtons>
                {
                    (flowStep === 1) || hidePrev ? <EmptyLeftSide></EmptyLeftSide> :
                        <PaymentButtonHollow
                            onClick={onPrevStep}
                            buttonTheme={isZeland ? 'black' : undefined}
                        >{t('back')}</PaymentButtonHollow>
                }
                <NextButtonComponent {...props} />
            </PaymentFlowButtons>
        </FlowWrapper>
    )
}
import {FC, PropsWithChildren, useMemo} from 'react'
import { PaymentButtonHollow } from '../CheckOut/styles'
import { FlowWrapper, PaymentFlowButtons, EmptyLeftSide, FlowChildren } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getCurrentContextName } from 'supporting'
import {selectorOrder, goToNextStep, goToPrevStep} from "../../slices/OrderSlice";
import styled from "styled-components";
import {PaymentButtonWithHint} from "../../components/PaymentButtonWithHint/PaymentButtonWithHint";
import { OrderSelectors } from 'shared/features/QueuePaymenrt/slices/selectors/orderSelectors'
import {FlowType} from "../../constants/flowType";
import {createPortal} from "react-dom";
import { withTheme } from 'styled-components'
import { keyframes } from 'styled-components'

const Spinner = withTheme((props) => {
    const {
      viewBox,
      cx,
      cy,
      radius,
      fill,
      width
    } = props
    return (
      <svg viewBox={viewBox || '22 22 44 44'}>
        <circle 
          cx={cx || '44'} 
          cy={cy || '44'}
          r={radius || '20.2'}
          fill={fill || 'none'} 
          strokeWidth={width || '3.6'}
        >
        </circle>
      </svg>
    );
  })


  const wrapperFrames = keyframes`
  0% {
    // transform: rotate(0deg);
    transform-origin: 50% 50%;
  }
  100% { 
    transform: rotate(360deg);
  }
`

const circleFrames = keyframes`
  0% {
    stroke-dasharray: 1px,200px;
    stroke-dashoffset: 0;
  }
  50% { 
    stroke-dasharray: 100px,200px;
    stroke-dashoffset: -15px;
  }
  100% { 
    stroke-dasharray: 100px,200px;
    stroke-dashoffset: -125px;
  }
`

export const SpinnerWrapper = styled.div<any>`
  animation: 1.4s linear 0s infinite normal none running ${wrapperFrames};
  min-width: 22px;
  width: 22px;
  height: 22px;
  display: inline-block;
  box-sizing: inherit;
  font-size: 1.875rem;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-weight: 400;
  line-height: 1.43;
  margin-left: ${p => p.ml !== undefined ? p.ml : 0}px;
  letter-spacing: 0.01071em;
  svg {
    display: block;
    box-sizing: inherit;
    overflow: hidden;
    font-size: 1.875rem;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 400;
    line-height: 1.43;
    letter-spacing: 0.01071em;
    margin: 0px;
    circle {
      animation: ${circleFrames} 1.4s ease-in-out infinite;  
      stroke-dasharray: 80px, 200px;
      stroke-dashoffset: 0px;
      stroke: currentColor;
      transform-origin: 0px 0px;
    }
  }
`

interface PaymentFlowWrapperProps {
    onPay?: () => void
    onPrev?: () => void
    onNext?: () => void
    nextDisabled?: boolean
    nextPortalSelector?: string
    disableHint?: string
    hidePrev?: boolean
    showSpinner?: boolean
    isPayStep?: boolean
    payTitle?: string
    wrapperProps?: any
    nextButtonRef?: any
    hideNext?: any
    fullWidth?: boolean
}

export const NextButtonComponent: FC<Partial<PropsWithChildren<PaymentFlowWrapperProps>>> = (props) => {
    const { onPay, onNext, nextDisabled, nextPortalSelector, showSpinner, isPayStep, disableHint, payTitle, hideNext, fullWidth } = props
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const flowStep = useSelector(OrderSelectors.flowStepSelector)
    const flowSize = useSelector(OrderSelectors.flowSizeSelector)
    const flowType = useSelector(OrderSelectors.flowTypeSelector)
    const contextName = getCurrentContextName()
    const isZeland = contextName === 'zeland';
    const deliveryAddress = (flowType === FlowType.BUY_PRINTED_SUBSCRIPTION || flowType === FlowType.GIFT_PRINTED_SUBSCRIPTION)

    const onNextStep = () => {
        if (typeof (onNext) === 'function') {
            onNext()
            return
        }
        dispatch(goToNextStep())
    }

    const nextOuter = nextPortalSelector && document.querySelector(nextPortalSelector)
    const nextButtonDefault = isPayStep || (flowStep === flowSize)
        ? <PaymentButtonWithHint
            hintText={disableHint}
            closeToRightCorner
            disabled={nextDisabled}
            onClick={onPay}
            buttonTheme={isZeland ? 'yellow' : undefined}
            isGift={flowType === FlowType.SEND_GIFT}
            fullWidth={fullWidth}
            noWidthLimitation={deliveryAddress}
        >
            {
                showSpinner
                    ? <SpinnerWrapper><Spinner /></SpinnerWrapper>
                    : payTitle || t('payment.pay')
            }
        </PaymentButtonWithHint>
        : <PaymentButtonWithHint
            hintText={disableHint}
            closeToRightCorner
            disabled={nextDisabled}
            onClick={onNextStep}
            isGift={flowType === FlowType.SEND_GIFT}
            fullWidth={fullWidth}
        >
            {t("next")}
        </PaymentButtonWithHint>

    const nextButtonComponent = useMemo(() => {
        if (hideNext) return
        if (nextPortalSelector && !nextOuter) return nextButtonDefault
        return nextOuter
            ? createPortal(nextButtonDefault, nextOuter)
            : nextButtonDefault
    }, [nextPortalSelector, nextOuter, hideNext, nextButtonDefault])

    return <>
        {nextButtonComponent}
    </>
}

export const PaymentFlowWrapper: FC<PropsWithChildren<PaymentFlowWrapperProps>> = (props) => {
    const { children, onPrev, hidePrev, wrapperProps = {} } = props
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { flowStep } = useSelector(selectorOrder)
    const contextName = getCurrentContextName()
    const isZeland = contextName === 'zeland';

    const onPrevStep = () => {
        if (typeof (onPrev) === 'function') {
            onPrev()
            return
        }
        dispatch(goToPrevStep())
    }

    return (
        <FlowWrapper {...wrapperProps}>
            <FlowChildren>
                {children}
            </FlowChildren>
            <PaymentFlowButtons>
                {
                    (flowStep === 1) || hidePrev ? <EmptyLeftSide></EmptyLeftSide> :
                        <PaymentButtonHollow
                            onClick={onPrevStep}
                            buttonTheme={isZeland ? 'black' : undefined}
                        >{t('back')}</PaymentButtonHollow>
                }
                <NextButtonComponent {...props} />
            </PaymentFlowButtons>
        </FlowWrapper>
    )
}
